import { msg } from '@lingui/macro'

import * as React from 'react'

import {
  TMAddPromoCodeVariables,
  TMRemovePromoCodeVariables,
  TQOrderPromoCodesVariables,
  useMAddPromoCode,
  useMRemovePromoCode,
  useQOrderPromoCodes,
} from '~graphql/generated/clientGraphql'
import { useTrans } from '~logic/i18n/useTrans'

import { useFormOrderCtx } from '../../../contexts/FormOrderProvider'

type TProps = {
  /**
   * a list of Qs that should be re-fetched
   * after adding/removing promo codes
   */
  refetchQueries?: string[]
}
/**
 * Holds an array of current promo codes of the order in the form.
 * Also provides M wrappers for adding and removing codes from that order.
 *
 * Intended to be used inside a context provider.
 */
export const usePromoCodeState = ({ refetchQueries }: TProps) => {
  const { trans } = useTrans()

  const order = useFormOrderCtx()
  const orderId = order?.id

  const [mAddCode] = useMAddPromoCode({ errorHandling: `major`, refetchQueries })
  const [mRemoveCode] = useMRemovePromoCode({ refetchQueries })

  const qPromoCodeVariables = React.useMemo<TQOrderPromoCodesVariables | undefined>(() => {
    return orderId ? { orderId } : undefined
  }, [orderId])

  const { data, loading } = useQOrderPromoCodes({
    skip: !qPromoCodeVariables,
    variables: qPromoCodeVariables,
  })

  // fire an M to add promo code to the order
  const addPromoCode = React.useCallback(
    async (inputValue: string) => {
      // return the result
      // (needed eg. to clear the promo input when the code was successfully added)
      const wasSuccessful = false

      if (!orderId) {
        return { wasSuccessful }
      }

      const code = inputValue.trim()

      if (code.length === 0) {
        return {
          wasSuccessful,
          message: trans(msg`Can’t add empty code`),
        }
      }

      const variables: TMAddPromoCodeVariables = {
        code,
        orderId,
      }

      const { data, errors } = await mAddCode({ variables })

      const hasErrors = errors instanceof Error || (errors && errors.length > 0)

      if (hasErrors) {
        const message = errors instanceof Error ? errors.message : errors[0].message

        return { wasSuccessful, message }
      }

      if (!data) {
        return { wasSuccessful }
      }

      if (data.result.errors.length > 0) {
        return { wasSuccessful, message: data.result.errors[0] }
      }

      // no need to work with the data here
      // because Apollo does automagically trigger all of the Qs reading this order’s data
      // meaning it will trigger a refetch of the Q above as well as the Cart Q!

      return { wasSuccessful: true }
    },
    [mAddCode, orderId, trans],
  )

  // fire an M to remove promo code from the order
  const removePromoCode = React.useCallback(
    async (codeId: ID) => {
      if (!orderId) {
        return
      }

      const variables: TMRemovePromoCodeVariables = {
        codeId,
        orderId,
      }

      await mRemoveCode({ variables })

      // no need to work with the data here
      // because Apollo does automagically trigger all of the Qs reading this order’s data
      // meaning it will trigger a refetch of the Q above as well as the Cart Q!
    },
    [mRemoveCode, orderId],
  )

  // this should always return up-to-date promo codes (it updates when adding/removing)
  return {
    promoCodes: data?.order.promoCodes ?? [],
    addPromoCode,
    removePromoCode,
    isLoading: !data || loading,
  }
}
